import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  PageShell, TabBar, Stat, StatusPill, Pill,
  LoadingBox, ErrorBox,
  useAdminApi, fmtDate, DateCell, Avatar,
  ActionButton, IconButton, useConfirmDialog, Icon, Hint,
} from "./_adminApi";

const ROLE = {
  superuser: ["violet", "Superuser"],
  staff:     ["blue",   "Staff"],
  regular:   ["neutral","Regular"],
};
function roleOf(u) {
  if (u.is_superuser) return "superuser";
  if (u.is_staff) return "staff";
  return "regular";
}

export default function UserDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useAdminApi();
  const activeTab = (location.hash || "#profile").replace(/^#/, "");
  const setTab = (t) => navigate({ hash: `#${t}` }, { replace: true });

  const [user, setUser] = useState(null);
  const [err, setErr] = useState(null);
  const [confirm, confirmModal] = useConfirmDialog();

  const load = () => {
    setErr(null);
    api.get(`admin/users/${id}/`).then((r) => setUser(r.data)).catch(setErr);
  };
  useEffect(load, [id]);

  const resetPassword = async () => {
    const pw = window.prompt(`Set a NEW password for ${user?.username}:\n\n(Minimum 8 characters. Hand it over manually — no email is sent.)`);
    if (!pw) return;
    try {
      await api.post(`admin/users/${id}/reset-password/`, { password: pw });
      alert("Password reset. Share the new password manually.");
    } catch (e) { alert(e?.response?.data?.error || e.message); }
  };

  const deleteUser = async () => {
    const ok = await confirm({
      title: `Delete ${user.first_name || user.username}?`,
      body: `This account will lose access everywhere. It can't be undone.`,
      detail: user.is_superuser ? "⚠ SUPERUSER — losing this account removes top-level access." : null,
      confirmLabel: "Delete user",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`admin/users/${id}/`);
      navigate("/dashboard/admin/users");
    } catch (e) { alert(e?.response?.data?.error || e?.response?.data?.detail || e.message); }
  };

  if (err) return <PageShell title="User"><ErrorBox error={err} /></PageShell>;
  if (!user) return <PageShell title="User"><LoadingBox /></PageShell>;

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username;
  const roleKey = roleOf(user);
  const rp = ROLE[roleKey] || ROLE.regular;

  return (
    <PageShell
      breadcrumb={[
        { label: "Admin" },
        { label: "Users", to: "/dashboard/admin/users" },
        { label: fullName },
      ]}
      title={<>{fullName} <StatusPill status={user.is_active ? "active" : "revoked"} /> <Pill kind={rp[0]}>{rp[1]}</Pill></>}
      subtitle={user.email}
      actions={
        <>
          <ActionButton variant="ghost" icon={<Icon.key />} onClick={resetPassword} title="Set a new password for this user">
            Reset password
          </ActionButton>
          <ActionButton variant="danger" icon={<Icon.trash />} onClick={deleteUser} title="Permanently delete this account">
            Delete
          </ActionButton>
        </>
      }
    >
      <div className="ad-stats">
        <Stat tone="teal"   icon={Icon.shield}   label="Role"       value={rp[1]} />
        <Stat tone="green"  icon={Icon.check}    label="Verified"   value={user.is_verified ? "Yes" : "—"} />
        <Stat tone="blue"   icon={Icon.activity} label="Online"     value={user.online ? "Live" : "Offline"} />
        <Stat tone="violet" icon={Icon.monitor}  label="Last login" value={user.last_login ? fmtDate(user.last_login).split(",")[0] : "Never"} />
      </div>

      <TabBar
        active={activeTab}
        onChange={setTab}
        tabs={[
          { key: "profile",  label: "Profile",  icon: Icon.users },
          { key: "sessions", label: "Sessions / Activity", icon: Icon.activity },
        ]}
      />
      {activeTab === "profile"  && <ProfileTab  user={user} onChanged={load} />}
      {activeTab === "sessions" && <SessionsTab userId={user.id} />}
      {confirmModal}
    </PageShell>
  );
}

function ProfileTab({ user, onChanged }) {
  const api = useAdminApi();
  const [form, setForm] = useState({
    first_name: user.first_name || "",
    last_name:  user.last_name  || "",
    is_active:  !!user.is_active,
    is_staff:   !!user.is_staff,
    is_superuser: !!user.is_superuser,
    is_verified:  !!user.is_verified,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setErr(null); setSaving(true);
    try { await api.patch(`admin/users/${user.id}/`, form); onChanged(); }
    catch (e) { setErr(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="ad-grid2">
      <div className="ad-card ad-sect">
        <h3><span className="ic"><Icon.users /></span>Identity</h3>
        {err && <ErrorBox error={err} />}
        <dl className="ad-dl">
          <dt>Username</dt>
          <dd><span className="mono">{user.username}</span></dd>

          <dt>Email</dt>
          <dd>{user.email}</dd>

          <dt>First name</dt>
          <dd><input className="inline-ctl auto" style={{ width: 180 }} value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} /></dd>

          <dt>Last name</dt>
          <dd><input className="inline-ctl auto" style={{ width: 180 }} value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} /></dd>

          <dt>User ID</dt>
          <dd><span className="mono" title={user.id}>{String(user.id).slice(0, 8)}…</span></dd>

          <dt>Joined</dt>
          <dd><DateCell iso={user.date_joined} /></dd>
        </dl>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <ActionButton onClick={save} disabled={saving} icon={<Icon.check />}>
            {saving ? "Saving…" : "Save profile"}
          </ActionButton>
        </div>
      </div>

      <div className="ad-card ad-sect">
        <h3><span className="ic"><Icon.shield /></span>Permissions &amp; status</h3>
        <dl className="ad-dl">
          <dt>Account active <Hint text="Inactive accounts cannot log in" /></dt>
          <dd><button type="button" className={`tgl${form.is_active ? " on" : ""}`} onClick={() => upd("is_active", !form.is_active)} aria-pressed={form.is_active} /></dd>
          <dt>Staff <Hint text="Can access this admin dashboard" /></dt>
          <dd><button type="button" className={`tgl${form.is_staff ? " on" : ""}`} onClick={() => upd("is_staff", !form.is_staff)} aria-pressed={form.is_staff} /></dd>
          <dt>Superuser <Hint text="Full access everywhere. Grant sparingly." /></dt>
          <dd><button type="button" className={`tgl${form.is_superuser ? " on" : ""}`} onClick={() => upd("is_superuser", !form.is_superuser)} aria-pressed={form.is_superuser} /></dd>
          <dt>Email verified</dt>
          <dd><button type="button" className={`tgl${form.is_verified ? " on" : ""}`} onClick={() => upd("is_verified", !form.is_verified)} aria-pressed={form.is_verified} /></dd>
          <dt>Role (legacy)</dt>
          <dd>{user.role_name || "—"}</dd>
          <dt>Chat user</dt>
          <dd>{user.is_chat_user ? "Yes" : "No"}</dd>
        </dl>
      </div>
    </div>
  );
}

function SessionsTab({ userId }) {
  const api = useAdminApi();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setErr(null);
    api.get(`admin/users/${userId}/sessions/`)
      .then((r) => setData(r.data))
      .catch(setErr);
  }, [userId]);

  if (err) return <ErrorBox error={err} />;
  if (!data) return <LoadingBox />;
  return (
    <div className="ad-card ad-sect" style={{ maxWidth: 600 }}>
      <h3><span className="ic"><Icon.activity /></span>Recent activity</h3>
      <dl className="ad-dl">
        <dt>Last login</dt><dd>{data.last_login ? <DateCell iso={data.last_login} /> : "Never"}</dd>
        <dt>Last seen</dt><dd>{data.last_seen ? <DateCell iso={data.last_seen} /> : "Never"}</dd>
        <dt>Account created</dt><dd><DateCell iso={data.date_joined} /></dd>
        <dt>Online now</dt><dd>{data.is_online ? <Pill kind="ok" dot>Live</Pill> : <Pill kind="neutral">Offline</Pill>}</dd>
        <dt>Account active</dt><dd>{data.is_active ? "Yes" : "No"}</dd>
        <dt>Is staff</dt><dd>{data.is_staff ? "Yes" : "No"}</dd>
      </dl>
      <p style={{ marginTop: 14, fontSize: 12, color: "var(--fg-4)", lineHeight: 1.5 }}>
        Per-session history (IP, user-agent per login) isn't tracked today — Django's default session table
        doesn't key sessions by user_id. Add a <code className="mono">UserLoginAudit</code> model if you need it.
      </p>
    </div>
  );
}
